// Phase 1A Step 35 — SIKA conversation cycle data.
// Source: Every Document/SIKA.md §2 (tone), §4 (goal categories A/B/E),
// §5 (conversation flow), §6 (multi-market plans), §10 (SIKA Pro tier).
//
// Each example is a real-world goal a Ghana/Nigeria user would type in
// plain language, paired with SIKA's plan response. Plans use the same
// odds×weeks compounding math the rest of the landing already runs.

export interface SikaPlan {
  goal: string;
  target: number;
  stake: number;
  weeks: number;
  odds: number;
  // Tier the plan would activate on. Single-chain plans run on SIKA Pro;
  // multi-market plans require Pro per SIKA.md §6.
  tier: "SIKA Pro" | "Pro";
  // Optional second/third chain rows shown for multi-market plans.
  chains?: ReadonlyArray<{ market: string; stake: number; odds: number; weeks: number }>;
}

export interface SikaTurn {
  category: "A" | "B" | "E";
  userPrompt: string;
  sikaResponse: string;
  plan: SikaPlan;
}

// Three turns covering Category A (consumer), B (major life), E (recurring).
// Tone matches §2 — direct, no corporate softeners, numbers up front.
export const SIKA_TURNS: ReadonlyArray<SikaTurn> = [
  {
    category: "A",
    userPrompt: "I want to buy a MacBook Pro M4.",
    sikaResponse:
      "GH₵22,000 at current import. GH₵300 opening at 3.0× weekly puts you there in week 7. The protected path keeps half on the table — you walk with GH₵21,300 even if Week 7 breaks.",
    plan: {
      goal: "MacBook Pro M4 · 14-inch",
      target: 22000,
      stake: 300,
      weeks: 7,
      odds: 3,
      tier: "SIKA Pro",
    },
  },
  {
    category: "B",
    userPrompt: "I want to buy a plot in East Legon.",
    sikaResponse:
      "Quarter-acre median is GH₵850,000. That's a multi-chain plan — three parallel chains across football, Forex, and crypto. 24 weeks at current rates. I'll show both paths before you commit.",
    plan: {
      goal: "Plot · East Legon",
      target: 850000,
      stake: 500,
      weeks: 24,
      odds: 3,
      tier: "Pro",
      chains: [
        { market: "Football · PITCH", stake: 500, odds: 3.0, weeks: 24 },
        { market: "Forex · PIP", stake: 800, odds: 2.0, weeks: 24 },
        { market: "Crypto · BLOCK", stake: 600, odds: 2.5, weeks: 24 },
      ],
    },
  },
  {
    category: "E",
    userPrompt: "Cover one term of school fees.",
    sikaResponse:
      "GH₵8,000 per term. GH₵200 opening at 2.5× weekly clears it in 5 weeks. Conservative chain — fewer agents debating, higher consensus threshold. Recurring, so I'll rebuild it next term.",
    plan: {
      goal: "School fees · 1 term",
      target: 8000,
      stake: 200,
      weeks: 5,
      odds: 2.5,
      tier: "SIKA Pro",
    },
  },
];
